import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { courseBaseByIdQueryOptions, courseByIdQueryOptions, endpoints } from "@vixi/api";
import { useTranslation } from "@vixi/localization";
import { Link, NavigateOptions, RouteOptionsById, router, RouterIds } from "@vixi/router";
import { COURSE_STATUS, COURSE_STATUS_LABELS, EditBasicCourseRequest } from "@vixi/types";
import {
  Button,
  cn,
  CourseCard,
  PageMainContent,
  useGoBack,
  useUiStore,
} from "@vixi/ui";
import { assert } from "@vixi/utils";
import { ComponentProps, useCallback } from "react";

// Constants
const routeId = "/_app/courses/$courseId/" satisfies RouterIds;
const Route = router.routesById[routeId];

const backRoute: NavigateOptions = { to: "/courses" };

export const CourseIdIndexPageOptions: RouteOptionsById<typeof routeId> = {
  component: RouteComponent,
  loader: ({ context: { queryClient }, params: { courseId } }) => {
    queryClient.ensureQueryData(courseByIdQueryOptions(queryClient, courseId));
    queryClient.ensureQueryData(courseBaseByIdQueryOptions(queryClient, courseId));
  },
};

const ManageCourseCard = (props: ComponentProps<"div">) => (
  <div
    {...props}
    className={cn(
      "bg-paper flex w-full items-center ps-5.5 gap-6.25 rounded max-h-[81px] hover:border-primary border-2 border-transparent transition-all duration-300 ",
      props.className,
    )}
  />
);

function RouteComponent() {
  useGoBack(backRoute);
  const { t, isRtl } = useTranslation();
  const { showConfirmDialog } = useUiStore();
  const queryClient = useQueryClient();

  const { courseId } = Route.useParams();
  const { data: course, isFetching } = useQuery(courseBaseByIdQueryOptions(queryClient, courseId));

  const pathParams = { courseId };

  const onUnpublishClicked = useCallback(() => {
    showConfirmDialog({
      title: "Confirmation.UnpublishCourse.Title",
      description: t("Confirmation.UnpublishCourse.Description"),
      confirmText: "Action.Unpublish",
      cancelText: "Action.Cancel",

      onConfirm: async () => {
        assert(!!course, "Course data is required to unpublish");

        await api.post(endpoints.editCourseBase, {
          courseId: parseInt(courseId),
          course: {
            ...course,
            categoryId: course?.subcategory?.id ?? course?.categoryId ?? 1,
            courseStatus: COURSE_STATUS.WAITING_UNPUBLISH,
          },
        } satisfies EditBasicCourseRequest);
        return true;
      },
    });
  }, [course, t, showConfirmDialog]);

  const onPublishClicked = useCallback(() => {
    showConfirmDialog({
      title: "Confirmation.PublishCourse.Title",
      description: t("Confirmation.PublishCourse.Description"),
      confirmText: "Action.Publish",
      cancelText: "Action.Cancel",

      onConfirm: async () => {
        assert(!!course, "Course data is required to publish");

        await api.post(endpoints.editCourseBase, {
          courseId: parseInt(courseId),
          course: {
            ...course,
            categoryId: course?.subcategory?.id ?? course?.categoryId ?? 1,
            courseStatus: COURSE_STATUS.WAITING_PUBLISH,
          },
        } satisfies EditBasicCourseRequest);

        queryClient.invalidateQueries({
          queryKey: courseBaseByIdQueryOptions(queryClient, courseId).queryKey,
        });
        return true;
      },
    });
  }, [course, t, showConfirmDialog]);

  return (
    <PageMainContent scrollable ps="medium">
      <div className="flex h-full gap-y-5">
        <div className="basis-full lg:basis-auto">
          <div className="pe-17.5 pt-7.5">
            <div className="w-75">
              <CourseCard
                loading={isFetching}
                disableLink
                course={course}
                image={course?.courseCharacters?.[0]?.url}
              />
          
            </div>
          </div>
        </div>

        <div className="basis-full self-stretch lg:grow lg:basis-auto pr-[82px]">
          <div className="ar:text-[16px] flex h-full flex-col gap-5 border-s-2 border-s-gray-300 pb-5 lg:ps-7.5">
            <Link to="/courses/$courseId/edit-lessons"
              params={pathParams}>
            <ManageCourseCard className="relative">
              <img src="/images/course-source/review.png" alt="" className="h-[59px] w-[59px]" />
              <div className="flex flex-col items-start ">
                <h2
                    className="text-[#334D6E] text-[20px] font-bold mt-3.5"
                >
                  {t("Action.EditLessons")}
                </h2>

                <p className="mb-4.5">{t("Courses.EditLessonsDescription")}</p>
              </div>
              <img src="/images/icons/rightarrow.png" alt="" className={cn("absolute top-[50%] translate-y-[-50%]", isRtl ? "left-6.25" : "right-6.25")} />
            </ManageCourseCard>
            </Link>

            <Link to="/courses/$courseId/edit-info"
              params={pathParams}>
              <ManageCourseCard className="relative">
                <img src="/images/course-source/editinfo.png" alt="" className="h-[56px] w-[56px]" />
                <div className="flex flex-col items-start ">
                  <h2

                    className="text-[#334D6E] text-[20px] font-bold mt-3.5"

                  >
                    {t("Action.EditInfo")}
                  </h2>

                  <p className="mb-4.5">{t("Courses.EditInfoDescription")}</p>
                </div>
                <img src="/images/icons/rightarrow.png" alt="" className={cn("absolute top-[50%] translate-y-[-50%]", isRtl ? "left-6.25" : "right-6.25")} />

              </ManageCourseCard>
              </Link>
      


            <Link to="/courses/$courseId/edit-avatars"
              params={pathParams}>
              <ManageCourseCard className="relative">
                <img src="/images/course-source/manage.png" alt="" className="h-[59px] w-[59px]" />

                <div className="flex flex-col items-start">
                  <h2

                    className="text-[#334D6E] text-[20px] font-bold mt-3.5"

                  >
                    {t("Action.EditCourseAvatars")}
                  </h2>

                  <p className="mb-4.5">{t("Courses.EditCourseAvatarsDescription")}</p>
                </div>
                <img src="/images/icons/rightarrow.png" alt="" className={cn("absolute top-[50%] translate-y-[-50%]", isRtl ? "left-6.25" : "right-6.25")} />


              </ManageCourseCard>
              </Link>
       

           {/* <ManageCourseCard className="relative">
              <img src="/images/course-source/manage.png" alt="" className="h-[83px] w-[83px]" />
              <div className="flex flex-col items-start gap-2.5">
                <Link
                className="text-[#334D6E] text-[20px] font-bold"
                  to="/courses/$courseId/manage-access"
                  params={pathParams}

                >
                  {t("Action.ManageAccess")}
                </Link>

                <p>{t("Courses.ManageAccessDescription")}</p>
              </div>
              <img src="/images/icons/rightarrow.png" alt="" className=" absolute right-6.25 top-12.5" />

            </ManageCourseCard>
          */}
          <Link to="/courses/$courseId/see-analytics"
                  params={pathParams}>
            <ManageCourseCard className="relative">
              <img src="/images/course-source/analysis.png" alt="" className="h-[59px] w-[59px]" />
              <div className="flex flex-col items-start ">
                <h2  
                  className="text-[#334D6E] text-[20px] font-bold mt-3.5"
                >
                  {t("Action.SeeAnalytics")}
                </h2>

                <p  className="mb-4.5">{t("Courses.SeeAnalyticsDescription")}</p>
              </div>
              <img src="/images/icons/rightarrow.png" alt="" className={cn("absolute top-[50%] translate-y-[-50%]", isRtl ? "left-6.25" : "right-6.25")} />
            </ManageCourseCard>
             </Link>
    

       {/*     {course?.courseStatus === COURSE_STATUS.PUBLISHED && (
              <ManageCourseCard>
                <Button variant="outlined" size="small" onClick={onUnpublishClicked}>
                  {t("Action.Unpublish")}
                </Button>

                <p>{t("Courses.UnpublishDescription")}</p>
              </ManageCourseCard>
            )}

            {course?.courseStatus === COURSE_STATUS.DRAFT && (
              <ManageCourseCard>
                <Button variant="outlined" size="small" onClick={onPublishClicked}>
                  {t("Action.Publish")}
                </Button>

                <p>{t("Courses.PublishDescription")}</p>
              </ManageCourseCard>
            )}*/} 
          </div>
        </div>
      </div>
    </PageMainContent>
  );
}
